import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ViewQuiltOutlinedIcon from "@mui/icons-material/ViewQuiltOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import { connect } from "react-redux";
import { updateDrawerOpen } from "../redux/actions/userDataActions";

import { ReactComponent as Settings } from "../assets/images/settings.svg";
import { ReactComponent as Users } from "../assets/images/users.svg";
import { ReactComponent as Roles } from "../assets/images/user_role.svg";
import { ReactComponent as Events } from "../assets/images/events.svg";
import { ReactComponent as Restaurant } from "../assets/images/restaurant.svg";
import { ReactComponent as CategoryIcon } from "../assets/images/category.svg";
import { ReactComponent as CustomerIcon } from "../assets/images/customers-dark.svg";
import logo from "../assets/images/new_logo.png";

import Navbar from "./Navbar";

const SIDEBAR_BG = "#ffffff";
const ACTIVE_BG = "#d6f1d8";       // olivv accent-soft
const ACTIVE_TEXT = "#053418";     // olivv forest
const INACTIVE_TEXT = "#1a1a1a";   // olivv ink
const SECTION_LABEL_COLOR = "#6b7368"; // olivv muted
const drawerWidth = 240;
const drawerWindowWidth = 75;

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: "20px 16px 16px",
  gap: 10,
  borderBottom: "1px solid #E5EAD9",
  marginBottom: 8,
}));

const nav_sections = [
  {
    label: null,
    items: [
      {
        page: "dashboard",
        label: "Dashboard",
        icon: "dashboard",
        href: "/dashboard",
      },
    ],
  },
  {
    label: "MANAGE",
    items: [
      {
        page: "restaurants",
        label: "Restaurants",
        icon: "restaurant",
        href: "/restaurants",
      },
      {
        page: "customers",
        label: "Customers",
        icon: "customers",
        href: "/customers",
      },
      {
        page: "users",
        label: "Users",
        icon: "users",
        href: "/users",
      },
      {
        page: "roles",
        label: "Roles",
        icon: "roles",
        href: "/roles",
      },
    ],
  },
  {
    label: "GLOBAL",
    items: [
      {
        page: "events",
        label: "Events",
        icon: "events",
        href: "/events",
      },
      {
        page: "categories",
        label: "Categories",
        icon: "categories",
        href: "/categories",
      },
      {
        page: "global_settings",
        label: "Settings",
        icon: "settings",
        href: "/global_settings",
      },
      {
        page: "socials",
        label: "Socials",
        icon: "socials",
        href: "/socials",
      },
      {
        page: "ui-config",
        label: "UI Config",
        icon: "ui_config",
        href: "/ui-config-global",
      },
    ],
  },
  {
    label: "CONTENT",
    items: [
      {
        page: "content-manager",
        label: "Content Manager",
        icon: "content_manager",
        href: "/content-manager",
      },
      {
        page: "content-generations",
        label: "Content Generations",
        icon: "content_generations",
        href: "/content-generations",
      },
      {
        page: "reference-content-manager",
        label: "Reference Content Manager",
        icon: "content_reference",
        href: "/reference-content-manager",
      },
    ],
  },
  {
    label: null,
    items: [
      {
        page: "subscriptions",
        label: "Subscriptions",
        icon: "subscriptions",
        href: "/subscriptions",
      },
    ],
  },
];

const IconComponent = ({ icon, isActive, size = 20 }) => {
  const color = isActive ? ACTIVE_TEXT : "#6b7368"; // olivv muted
  const svgProps = { height: size, width: size, style: { color } };
  switch (icon) {
    case "dashboard":
      return <DashboardOutlinedIcon sx={{ fontSize: size, color }} />;
    case "restaurant":
      return <Restaurant {...svgProps} />;
    case "customers":
      return <CustomerIcon {...svgProps} />;
    case "users":
      return <Users {...svgProps} />;
    case "roles":
      return <Roles {...svgProps} />;
    case "events":
      return <Events {...svgProps} />;
    case "categories":
      return <CategoryIcon {...svgProps} />;
    case "settings":
      return <Settings {...svgProps} />;
    case "socials":
      return <ShareOutlinedIcon sx={{ fontSize: size, color }} />;
    case "ui_config":
      return <ViewQuiltOutlinedIcon sx={{ fontSize: size, color }} />;
    case "content_manager":
      return <PhotoLibraryOutlinedIcon sx={{ fontSize: size, color }} />;
    case "content_generations":
      return <AutoAwesomeOutlinedIcon sx={{ fontSize: size, color }} />;
    case "content_reference":
      return <CollectionsBookmarkOutlinedIcon sx={{ fontSize: size, color }} />;
    case "subscriptions":
      return <InventoryOutlinedIcon sx={{ fontSize: size, color }} />;
    default:
      return null;
  }
};

const Sidebar = (props) => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(props.drawerOpen);
  const [mobileView, setMobileView] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setMobileView(window.innerWidth <= 990);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
    props.updateDrawerOpen(!mobileOpen);
  };

  const isExpanded = mobileOpen;

  const SideDrawer = () => (
    <Box sx={styles.mainBox}>
      <DrawerHeader>
        <Box sx={styles.logoCircle}>
          <img src={logo} alt="F" style={{ width: 28, height: 28, objectFit: "contain" }} />
        </Box>
        {isExpanded && (
          <Box>
            <Typography sx={styles.brandName}>Foodo</Typography>
            <Typography sx={styles.brandSub}>Control Plane</Typography>
          </Box>
        )}
      </DrawerHeader>

      <Box sx={{ overflowY: "auto", flex: 1, px: isExpanded ? 1.5 : 0.75 }}>
        {nav_sections.map((section, si) => (
          <Box key={si} sx={{ mb: 0.5 }}>
            {section.label && isExpanded && (
              <Typography sx={styles.sectionLabel}>{section.label}</Typography>
            )}
            {section.label && !isExpanded && <Box sx={{ height: 8 }} />}
            <List disablePadding>
              {section.items.map((item) => {
                const isActive = props.page === item.page;
                return (
                  <ListItem
                    button
                    key={item.page}
                    onClick={() => navigate(item.href)}
                    sx={{
                      ...styles.navItem,
                      backgroundColor: isActive ? ACTIVE_BG : "transparent",
                      "&:hover": {
                        backgroundColor: isActive ? ACTIVE_BG : "#f0f5ee",
                      },
                      justifyContent: isExpanded ? "flex-start" : "center",
                      px: isExpanded ? 1.5 : 1,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: isExpanded ? 1.5 : 0,
                        justifyContent: "center",
                      }}
                    >
                      <IconComponent icon={item.icon} isActive={isActive} size={20} />
                    </ListItemIcon>
                    {isExpanded && (
                      <ListItemText
                        primary={item.label}
                        sx={{
                          "& .MuiTypography-root": {
                            fontFamily: "GilroySemiBold",
                            fontSize: 13.5,
                            color: isActive ? ACTIVE_TEXT : INACTIVE_TEXT,
                            fontWeight: isActive ? 700 : 500,
                          },
                        }}
                      />
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>
    </Box>
  );

  const container =
    props.window !== undefined ? () => props.window().document.body : undefined;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <Navbar open={mobileOpen} handleDrawer={handleDrawerToggle} {...props} />
      <Box
        component="nav"
        sx={{
          width: { sm: !isExpanded ? drawerWindowWidth : drawerWidth },
          flexShrink: { sm: 0 },
        }}
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileView ? mobileOpen : false}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: !isExpanded ? drawerWindowWidth : drawerWidth,
              borderRightWidth: 0,
              borderRight: "1px solid #E5EAD9",
            },
          }}
        >
          <SideDrawer />
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: !isExpanded ? drawerWindowWidth : drawerWidth,
              borderRightWidth: 0,
              borderRight: "1px solid #E5EAD9",
            },
          }}
          open
        >
          <SideDrawer />
        </Drawer>
      </Box>
    </Box>
  );
};

const mapDispatchToProps = (dispatch) => ({
  updateDrawerOpen: (data) => dispatch(updateDrawerOpen(data)),
});

const mapStateToProps = (state) => ({
  drawerOpen: state.userData.drawerOpen,
});

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);

const styles = {
  mainBox: {
    backgroundColor: SIDEBAR_BG,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  logoCircle: {
    width: 36,
    height: 36,
    borderRadius: "10px",
    backgroundColor: "#00c853",   // olivv accent
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  brandName: {
    fontFamily: "GilroyBold",
    fontSize: 15,
    color: "#053418",
    lineHeight: 1.2,
  },
  brandSub: {
    fontFamily: "GilroyRegular",
    fontSize: 11,
    color: SECTION_LABEL_COLOR,
    lineHeight: 1,
  },
  sectionLabel: {
    fontFamily: "GilroySemiBold",
    fontSize: 10.5,
    color: SECTION_LABEL_COLOR,
    letterSpacing: "0.08em",
    px: 1.5,
    py: 0.75,
    mt: 1,
  },
  navItem: {
    borderRadius: "8px",
    mb: 0.25,
    height: 40,
    transition: "background-color 0.15s ease",
  },
};
